import React, { FC } from 'react';
import {
  STATUS_DONE,
  STATUS_WAITING_FOR,
} from '@kontentino/kontentino-constants/Posts';
import { useTranslation } from 'react-i18next';
import { useUser } from 'modules/user/userSelector';
import AssignedUsersView from 'components/shared/detail/AssignedUsersView';
import { useMutation } from 'react-query';
import PostApi from 'api/post';
import {
  usePostOverviewOptimisticQueryUpdater,
  usePostOverviewQuery,
} from 'app/modules/posts/modules/post/hooks/usePostOverview';
import { PostPopups } from 'app/modules/posts/modules/post/hooks/usePostPopups';
import AccordionWidgetIcon from 'app/modules/posts/components/accordionWidget/AccordionWidgetIcon';
import AccordionWidget from 'app/modules/posts/components/AccordionWidget';
import AccordionWidgetTitle from 'app/modules/posts/components/accordionWidget/AccordionWidgetTitle';
import { Button, Chip } from '@kontentino/ui';
import { stopPropagation } from 'utils/mouseEvent';

type Props = {
  id: number;
  showPopup(popup: PostPopups): void;
  accordionProps?: Partial<React.ComponentProps<typeof AccordionWidget>>;
};

const AssignedTasks: FC<Props> = (props) => {
  const { t } = useTranslation();
  const user = useUser();
  const { data, isLoading, isError } = usePostOverviewQuery(props.id);
  const updater = usePostOverviewOptimisticQueryUpdater(props.id);
  const toggle = useMutation(PostApi.toggleAssignedTaskCompletion, {
    onMutate(val) {
      updater.update((prevPost) =>
        updater.transformers.assignedTaskToggle(
          val.taskId,
          val.userId,
          prevPost,
        ),
      );
    },
    onSuccess() {
      updater.invalidateData();
    },
  });

  if (data?.status === STATUS_WAITING_FOR || data?.status === STATUS_DONE) {
    return (
      <AccordionWidget
        icon={<AccordionWidgetIcon variant="tasks" />}
        title={<AccordionWidgetTitle variant="tasks" />}
        titleSuffix={((count: number | undefined) => (
          <>
            <Button
              onClick={stopPropagation(() =>
                props.showPopup(PostPopups.AssignTask),
              )}
              size="small"
              variant="tertiary"
            >
              + {t('reassignTasks')}
            </Button>
            {!!count && (
              <Chip variant="alert" className="tw-ml-4">
                {count}
              </Chip>
            )}
          </>
        ))(
          data?.assigned_users.filter(({ completed_at }) => !completed_at)
            .length,
        )}
        {...props.accordionProps}
      >
        <AssignedUsersView
          isError={isError}
          isLoading={isLoading}
          userId={user.id}
          assignedUsers={data.assigned_users}
          onTaskToggle={(assignedUser) =>
            toggle.mutate({
              postId: props.id,
              userId: assignedUser.id,
              taskId: assignedUser.task.id,
              completed: !assignedUser.task.completed_at,
            })
          }
        />
      </AccordionWidget>
    );
  }

  return null;
};

export default AssignedTasks;
